import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { IDoctorBasic } from "../../types";
import { CircleUserRound, Video, Calendar, Hospital } from "lucide-react";
import { useAppointmentData, AppointmentDataContextType } from "../../contexts/appointment-data-context";

function DoctorCard({
  professionalId,
  profilePhoto,
  fullName,
  specialities,
  yearOfExperience,
  subDistrictOrCity,
  fee,
  isTeleconsultationEnable,
  isInClinicConsultationEnable,
}: IDoctorBasic) {
  const navigate = useNavigate();
  const { data, setData } = useAppointmentData();

  const handleAppointmentClick = (e: React.MouseEvent, type: "WALK_IN" | "TELECONSULTATION") => {
    e.preventDefault();
    e.stopPropagation();

    setData({
      patient: data?.patient,
      appointmentType: type,
      slotTiming: data?.slotTiming,
      slotDate: data?.slotDate,
      dayOfWeek: data?.dayOfWeek,
      symptoms: data?.symptoms ?? [],
      healthRecords: data?.healthRecords,
      healthRecordPhysician: data?.healthRecordPhysician,
      branchDetails: data?.branchDetails ?? []
    });

    navigate(`/doctors/${professionalId}`);
  };

  return (
    <div className="max-sm:py-6 max-sm:first:pt-0 sm:rounded-lg sm:border sm:p-2 lg:p-4 hover:shadow-md transition-shadow h-full">
      {/* Use flexbox to create equal height cards with buttons at bottom */}
      <div className="flex flex-col h-full">
        {/* Doctor Info Section - takes up available space */}
        <div className="flex-1">
          <div className="flex gap-3 mb-4">
            {profilePhoto ? (
              <img
                src={`${profilePhoto}`}
                alt={fullName || "Doctor"}
                className="size-14 rounded-full object-cover sm:size-18"
              />
            ) : (
              <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-muted sm:size-18">
                <CircleUserRound className="size-full" strokeWidth={1} />
              </div>
            )}

            <div className="flex-1">
              <h3 className="text-sm font-bold sm:text-lg">
                Dr {fullName || "Unknown"}
              </h3>
              <p className="mb-0.5 text-xs text-muted-foreground sm:text-sm">
                {specialities && specialities.length > 0 ? (
                  <>{specialities.join(", ")}, </>
                ) : (
                  "No specialties"
                )}
                {yearOfExperience != null
                  ? `${yearOfExperience} Years Exp.`
                  : "No experience info"}
              </p>
              {subDistrictOrCity?.trim() ? (
                <p className="mb-1 text-xs font-bold text-muted-foreground sm:text-sm">
                  {subDistrictOrCity}
                </p>
              ) : null}
              <p className="text-sm font-bold sm:text-base">
                Fee: ₹{fee != null ? fee : "NA"}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons Section - Always at bottom */}
        <div className="mt-auto">
          {(isTeleconsultationEnable || isInClinicConsultationEnable) && <p className="text-sm font-medium text-primary text-center mb-1">
            Book Appointment
          </p>}
          <div className={`grid gap-3 ${isTeleconsultationEnable ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {isTeleconsultationEnable && (
              <Button
                onClick={(e) => handleAppointmentClick(e, "TELECONSULTATION")}
                className="h-10 px-4 text-sm font-medium"
                variant="outline"
              >
                <Video className="w-4 h-4 mr-2" />
                Teleconsultation
              </Button>
            )}

            {isInClinicConsultationEnable && <Button
              onClick={(e) => handleAppointmentClick(e, "WALK_IN")}
              className="h-10 px-4 text-sm font-medium"
              variant="default"
            >
              <Hospital className="w-4 h-4 mr-2" />
              In-clinic
            </Button>}

            {!isInClinicConsultationEnable && !isTeleconsultationEnable && (
              <div className="flex items-center justify-center">
                <p>Not Available Now</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DoctorCard;
